// The pure half of the service worker's push handler: payload in, notification out. It lives
// outside src/sw.ts so it can be tested without a worker, and imports nothing so the worker stays small.
export interface NotificationSpec {
  readonly title: string;
  readonly options: {
    readonly body: string;
    readonly tag: string;
    readonly icon: string;
    /** `route` is the app route a tap opens (social pushes); absent, a tap opens the app. */
    readonly data: { readonly url: string; readonly route?: string };
  };
  /** A social push changes the inbox count; an update push does not. */
  readonly social: boolean;
}

// Used when a push arrives empty or unreadable: the browser insists a notification is shown, and
// this one says only what every update push says.
const FALLBACK = { title: "proef", body: "A new version of proef is ready." } as const;
const UPDATE_TAG = "proef-update";

// An app route the hash router knows: a path, maybe a query, never another origin or a script.
const APP_ROUTE = /^\/(?!\/)[\w\-./?=&%:]*$/;

/** Whether `value` is a route inside the app, safe to put after `#` on the app's own address. */
export function isAppRoute(value: unknown): value is string {
  return typeof value === "string" && value.length <= 512 && APP_ROUTE.test(value);
}

function readText(value: unknown, key: "title" | "body" | "tag"): string | undefined {
  if (typeof value !== "object" || value === null || !(key in value)) return undefined;
  const text: unknown = Reflect.get(value, key);
  return typeof text === "string" && text !== "" ? text.slice(0, 200) : undefined;
}

function parse(raw: string | undefined): unknown {
  if (raw === undefined) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** Maps a push's text body to the notification to show; `scope` is the worker's registration scope. */
export function notificationFromPush(raw: string | undefined, scope: string): NotificationSpec {
  const payload = parse(raw);
  const route =
    typeof payload === "object" && payload !== null ? Reflect.get(payload, "url") : undefined;
  const social = isAppRoute(route);
  return {
    title: readText(payload, "title") ?? FALLBACK.title,
    options: {
      body: readText(payload, "body") ?? FALLBACK.body,
      // A social push carries its own tag (kind and subject), so a repeat about the same thing
      // replaces it; every update push shares one.
      tag: (social ? readText(payload, "tag") : undefined) ?? UPDATE_TAG,
      icon: `${scope}icons/icon-192.png`,
      data: social ? { url: `${scope}#${route}`, route } : { url: scope },
    },
    social,
  };
}
